/* eslint-disable camelcase */
import _, { first } from 'lodash';
import { GA4EcommerceItem, GA4Item } from '../app/store/analytics/ga4/types';
import { IProductDataState } from '../app/store/product/productOverview/types';
import { ISearchParamsPagesState } from '../app/store/searchParams/types';
import { IProductInsuranceState } from '../app/store/product/insurance/types';
import { Purchases } from '../constants/Purchases';
import { IProductPrepared } from '../entities/product/types';
import { IBasketOrderItem } from '../entities/manhattan/order/types';
import { createFilterStringFromParams, getColorNameFromProduct } from './gtmUtils';
import { getSectionFromUrl, removeDotFromPrice } from './util';
import { OrderItemsTypes } from '../constants/OrderItemsTypes';
import { TransitionSteps } from '../constants/TransitionSteps';
import { OrderTypes } from '../constants/OrderTypes';
import Consumers from '../constants/Consumers';
import Accessories from '../constants/Accessories';
import { AnalyticsCampaignType, ISoftOfferPackage } from '../entities/campaigns/types';
import SoftOffer from '../entities/campaigns/offers/SoftOffer';

export const HOME_BRAND = 'YETTEL';
export const CASH = 'CASH';
export const TARIFF_CATEGORY = 'PLAN';
export const DELIVERY = 'DELIVERY';

const isPageACosmosPurchase = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('purchase') === 'cosmos';
};

const safelyConvertValueToRSD = (value: string | number) => {
  return Number(value) > 0 ? Math.floor(Number(value) / 100) : 0;
};

export const mapProductToGa4Item: (product: IProductPrepared, index: number) => GA4Item = (product, index) => {
  const adjustedIndex = index + 1;
  return {
    ...getBasicGa4ItemValues(product),
    position: adjustedIndex,
  };
};

export const mapDeviceListItemToEcommerceGa4Item: (product: IProductPrepared) => GA4EcommerceItem = (product) => {
  const isCosmos = isPageACosmosPurchase();

  return {
    item_id: Number(product.phoneId ?? product.id),
    item_name: `${product.manufacturer?.name} ${product.model}`,
    item_brand: product.manufacturer?.name,
    affiliation: 'Webshop E2E',
    item_category: 'Devices',
    item_category2: product.productLine?.name,
    item_category3: product.productLine?.name,
    item_category4: 'Postpaid',
    item_category5: getUserType(),
    price: Number(removeDotFromPrice(isCosmos ? product.prices.displayCreditPriceInRate : product.prices.displayPrice)),
    quantity: 1,
  };
};

export const mapDeviceOverviewToEcommerceGa4Item: (product: IProductPrepared, tariffId: number) => GA4EcommerceItem = (
  product,
  tariffId
) => {
  let price = 0;
  const isCosmos = isPageACosmosPurchase();
  const tariffPackage = product.tariffPackages?.find((tp) => tp.id === tariffId);

  if (tariffPackage) {
    const priceObject = first(tariffPackage.prices[isCosmos ? 'cosmos' : 'subsidy']?.filter((tp: any) => tp.selected));
    price = priceObject ? safelyConvertValueToRSD(Number(removeDotFromPrice((priceObject as any)?.price))) : 0;
  }

  return {
    item_id: Number(product.phoneId ?? product.id),
    item_name: `${product.manufacturer?.name} ${product.model}`,
    item_brand: product.manufacturer?.name,
    affiliation: 'Webshop E2E',
    item_category: 'Devices',
    item_category2: product.productLine?.name,
    item_category3: product.productLine?.name,
    item_category4: 'Postpaid',
    item_category5: getUserType(),
    item_variant: '',
    price: 0,
    quantity: 1,
  };
};

export const getDeviceOverviewSelectedTariff: (
  product: IProductPrepared,
  tariffId: number
) => GA4EcommerceItem | null = (product, tariffId) => {
  const tariffPackage = product.tariffPackages?.find((tp) => tp.id === tariffId);
  if (!tariffPackage) return null;

  return {
    item_id: tariffPackage.id,
    item_name: tariffPackage.name,
    affiliation: 'Webshop E2E',
    coupon: '',
    item_brand: 'Yettel',
    item_category: 'Packages',
    item_category2: 'Mobile',
    item_category3: getTariffPackageTypeFromName(tariffPackage.name),
    item_category4: 'Postpaid',
    item_category5: getUserType(),
    price: 0,
    quantity: 1,
    package_details: getPackageDetails(tariffPackage),
  };
};

export const createListName: (
  searchParams: ISearchParamsPagesState['productList'],
  product?: IProductPrepared
) => string = (searchParams, product) => {
  const listValue = [];
  const delimiter = ' | ';

  listValue.push(searchParams.purchase ? (searchParams.purchase === 'cosmos' ? 'NA RATE' : 'STANDARD') : 'NA RATE');
  listValue.push(product?.productLine?.productLine.toUpperCase());
  listValue.push(_.trim(createFilterStringFromParams(searchParams, product?.tariffPackages ?? []), delimiter));

  return listValue.filter((isValueTruthy) => Boolean(isValueTruthy)).join(delimiter);
};

export const getOverviewGa4ItemValues: (product: IProductPrepared, { color }: IProductDataState) => GA4Item = (
  product,
  { color }
) => {
  return {
    ...getBasicGa4ItemValues(product),
    item_variant: getColorNameFromProduct(product, color),
    quantity: 1,
  };
};

export const getOverviewGa4TariffValues: (product: IProductPrepared, productData: IProductDataState) => GA4Item = (
  product,
  productData
) => {
  const { cosmosTariffPackages, subsidyTariffPackages } = product;
  const { purchase, cosmosInstallments, contract } = productData;
  const { COSMOS } = Purchases;

  let tariffPackageGroup;
  if (purchase === COSMOS) {
    tariffPackageGroup = cosmosTariffPackages;
  } else {
    tariffPackageGroup = subsidyTariffPackages;
  }

  const tariffPackage = _.first(tariffPackageGroup?.filter((t) => t.id === productData.tariff));

  if (!tariffPackage) {
    return {
      item_id: 0,
      item_name: '',
      item_brand: HOME_BRAND,
      item_category: TARIFF_CATEGORY,
      price: 0,
      quantity: 0,
    };
  }

  const { id, name, cosmosSubscription, subscription } = tariffPackage;

  return {
    item_id: id,
    item_name: name,
    item_brand: HOME_BRAND,
    item_category: TARIFF_CATEGORY,
    price: purchase === COSMOS ? cosmosSubscription : subscription,
    quantity: purchase === COSMOS ? cosmosInstallments : contract,
  };
};

export const getOverviewGa4ParticipationValues: (productData: IProductDataState) => GA4Item | null = (productData) => {
  if (Number(productData.participation) > 0) {
    return {
      item_id: 1,
      item_name: CASH,
      item_brand: HOME_BRAND,
      item_category: CASH,
      price: Number(productData.participation),
      quantity: 1,
    };
  }

  return null;
};

export const getBasicGa4ItemValues: (product: IProductPrepared) => GA4Item = (product) => {
  return {
    item_id: Number(product.phoneId ?? product.id),
    item_name: `${product.manufacturer?.name} ${product.model}`,
    item_brand: product.manufacturer?.name,
    item_category: product.productLine?.productLine,
    price: Number(
      removeDotFromPrice(
        product.prices?.displayCreditPrice || product.displayCreditPrice || product.prices?.displayPrice
      )
    ),
  };
};

export const mapOrderItemsToGa4Items: (items: IBasketOrderItem[]) => GA4Item[] = (items) => {
  return items.map((item: IBasketOrderItem) => {
    return {
      item_id: item.id ?? 0,
      item_name: getOrderItemNameByType(item),
      item_brand: item.manufacturer ?? HOME_BRAND,
      item_category: getOrderItemCategoryByType(item),
      item_variant: item.color?.color ?? '',
      discount: getOrderItemDiscount(item),
      price: getPriceForOrderItemType(item),
      quanity: getOrderItemQuantityByType(item),
    };
  });
};

const getPriceForOrderItemType: (item: IBasketOrderItem) => number = (item) => {
  const { type, price } = item;

  if (type === OrderItemsTypes.STB) {
    if (price?.paymentMethod === 'monthly') {
      return ((Number(price?.installmentWithIncludedDiscount) || 1) / 100) * Number(price?.installments);
    }

    return (Number(price?.amount) || 1) / 100;
  }

  if (
    [OrderItemsTypes.TARIFF_PACKAGE, OrderItemsTypes.BUNDLE_PACKAGE, OrderItemsTypes.INSURANCE].includes(type as any)
  ) {
    if ((price?.discount as number) > 0) {
      return ((price?.installmentWithIncludedDiscount as number) / 100) * (price?.installments ?? 1);
    }

    return ((price?.installmentPrice as number) / 100) * (price?.installments ?? 1);
  }

  return Number(price?.amount) > 0 ? (price?.amount as number) / 100 : 0;
};

const getOrderItemNameByType: (item: IBasketOrderItem) => string = (item) => {
  const { type, name } = item;

  switch (type) {
  case OrderItemsTypes.INSURANCE:
    return 'INSURANCE';
  case OrderItemsTypes.COSMOS_PARTICIPATION:
    return CASH;
  case OrderItemsTypes.DELIVERY_COST:
    return 'DELIVERY';
  case OrderItemsTypes.MSISDN:
    return 'MSISDN';
  default:
    return name;
  }
};

export const getOrderItemCategoryByType: (item: IBasketOrderItem) => string = (item) => {
  const { type } = item;

  switch (type) {
  case OrderItemsTypes.TARIFF_PACKAGE:
    return TARIFF_CATEGORY;
  case OrderItemsTypes.COSMOS_PARTICIPATION:
    return CASH;
  default:
    return type;
  }
};

export const getOrderItemDiscount: (item: IBasketOrderItem) => number = (item) => {
  if (item.type === OrderItemsTypes.TARIFF_PACKAGE) {
    if ((item.price?.installments as number) > 0) {
      return (Number(item.price?.discount) / 100) * (item.price?.installments as number);
    }
    return 0;
  }

  return item.price?.discount ? Number(item.price?.discount) / 100 : 0;
};

export const getOrderItemQuantityByType: (item: IBasketOrderItem) => number = (item) => {
  const { type } = item;

  switch (type) {
  case OrderItemsTypes.BUNDLE_PACKAGE:
  case OrderItemsTypes.TARIFF_PACKAGE:
    return item.price?.installments ?? 1;
  default:
    return item.quantity ?? 1;
  }
};

export const mapCampaignItemTPToGA4Item: (tp: any) => GA4Item = (tp) => {
  return {
    item_id: tp.id,
    item_name: tp.name,
    item_category: TARIFF_CATEGORY,
    price: tp.subscription,
  };
};

export const DEFAULT_USER_PROPERTIES = {
  userType: { value: 'fizicko' },
  interactionType: { value: 'web' },
  content: { value: 'webshop' },
};

export const DEFAULT_MOB_USER_PROPERTIES = {
  user_actualTariffName: '',
  user_carrierType: '',
  user_customerType: '',
  user_propertyType: 'webshop',
  user_planType: '',
  user_isp: '',
};

export const DEFAULT_MOB_USER_PROTERTIES_NEW = {
  userType: getUserType(),
  interactionType: 'web',
  actualTariffName: '',
  planType: '',
  propertyType: '',
  customerType: '',
  carrierType: '',
};

export const GA4 = 'ga4';

export const DEFAULT_GA4_PROPS = {
  event: GA4,
  non_personalized_ads: false,
  user_properties: DEFAULT_USER_PROPERTIES,
};

export const DEFAULT_GA4_MOB_PROPS = {
  event: GA4,
  cid: '',
  user_properties: DEFAULT_MOB_USER_PROPERTIES,
};

export const mapInsuranceToGA4Item: (productInsurances: IProductInsuranceState) => GA4Item[] = (productInsurances) => {
  const { selectedInsurance } = productInsurances;
  const insuranceData = productInsurances?.hisPolicies.find(
    (insurance: { id: number }) => insurance.id === selectedInsurance?.policyId
  );
  const name = insuranceData ? insuranceData.policyType.name : '';
  const quantity = productInsurances?.numberPriceRate ? productInsurances.numberPriceRate : 0;
  const price = selectedInsurance?.price ? selectedInsurance.price : 0;

  return [
    {
      item_id: insuranceData?.id,
      item_name: name,
      quantity: Number(quantity),
      item_category: 'Insurance',
      price,
    },
  ];
};

export const generateCheckoutEventName: (orderType: string, stepName: string) => string = (orderType, stepName) => {
  if (orderType === OrderTypes.TELCO) {
    if (stepName === TransitionSteps.TELCO.CHOOSE_NUMBER_STATE) {
      return 'mob_checkout_1_izbor_broja';
    }
    if (stepName === TransitionSteps.TELCO.CUSTOMER_CHECK_STATE) {
      return 'mob_checkout_2_provera_podataka';
    }
    if (stepName === TransitionSteps.TELCO.PAYMENT_OPTION_STATE) {
      return 'mob_checkout_3_nacin_placanja';
    }
    if (stepName === TransitionSteps.TELCO.PERSONAL_DATA_STATE) {
      return 'mob_checkout_4_licni_podaci';
    }
    if (stepName === TransitionSteps.TELCO.DELIVERY_INFO_STATE) {
      return 'mob_checkout_5_adresa_za_dostavu_posiljke';
    }
    if (stepName === TransitionSteps.TELCO.CONFIRM_ORDER_STATE) {
      return 'mob_checkout_6_pregled_porudzbine';
    }
  }

  if (orderType === OrderTypes.ACCESSORY) {
    if (stepName === TransitionSteps.ACCESSORY.CUSTOMER_CHECK_STATE) {
      return 'mob_checkout_1_provera_podataka';
    }
    if (stepName === TransitionSteps.ACCESSORY.CHOOSE_EXISTING_NUMBER) {
      return 'mob_checkout_2_izbor_broja';
    }
    if (stepName === TransitionSteps.ACCESSORY.PAYMENT_OPTION_STATE) {
      return 'mob_checkout_3_nacin_placanja';
    }
    if (stepName === TransitionSteps.ACCESSORY.PERSONAL_DATA_STATE) {
      return 'mob_checkout_4_licni_podaci';
    }
    if (stepName === TransitionSteps.ACCESSORY.DELIVERY_INFO_STATE) {
      return 'mob_checkout_5_adresa_za_dostavu_posiljke';
    }
    if (stepName === TransitionSteps.COMMON.CONFIRM_ORDER_STATE) {
      return 'mob_checkout_6_pregled_porudzbine';
    }
  }

  if (orderType === OrderTypes.COMPLEX) {
    if (stepName === TransitionSteps.COMPLEX.CUSTOMER_CHECK_STATE) {
      return 'mob_checkout_1_provera_podataka';
    }
    if (stepName === TransitionSteps.COMPLEX.CHOOSE_NUMBER_STATE) {
      return 'mob_checkout_2_izbor_broja';
    }
    if (stepName === TransitionSteps.COMPLEX.PAYMENT_OPTION_STATE) {
      return 'mob_checkout_3_nacin_placanja';
    }
    if (stepName === TransitionSteps.COMPLEX.PERSONAL_DATA_STATE) {
      return 'mob_checkout_4_licni_podaci';
    }
    if (stepName === TransitionSteps.COMPLEX.DELIVERY_INFO_STATE) {
      return 'mob_checkout_5_adresa_za_dostavu_posiljke';
    }
    if (stepName === TransitionSteps.COMMON.CONFIRM_ORDER_STATE) {
      return 'mob_checkout_6_pregled_porudzbine';
    }
  }

  if (stepName === TransitionSteps.COMMON.CONFIRM_ORDER_STATE) {
    return 'mob_purchase';
  }

  if (stepName === 'viewCart') {
    return 'mob_view_cart';
  }

  return '';
};

export const getTariffPackageTypeFromName = (tpName: string) => {
  if (tpName.includes('Smart')) return 'Go Smart';
  if (tpName.includes('Total')) return 'Total Packages';
  if (tpName.includes('Go')) return 'Go Packages';
  if (tpName.includes('Surfuj') || tpName.includes('internet')) return 'Internet';

  return '';
};

const getPackageDetails = (tp: any) => {
  return `${tp.name}|${tp.id}|Mobile|${getTariffPackageTypeFromName(tp.name)}|Postpaid|${getUserType()}`;
};

function getUserType() {
  const isConsumer = getSectionFromUrl() === Consumers.PRIVATE_CONSUMER;
  return isConsumer ? 'Consumer' : 'Business';
}

const parseTarrifPackageFromBasket = (tp: any) => {
  return {
    item_id: tp.id,
    item_name: tp.name,
    affiliation: 'WebshopE2E',
    coupon: '',
    item_brand: 'Yettel',
    item_category: 'Packages',
    item_category2: 'Mobile',
    item_category3: getTariffPackageTypeFromName(tp.name),
    item_category4: 'Postpaid',
    item_category5: getUserType(),
    item_list_id: '',
    item_list_name: '',
    price: safelyConvertValueToRSD(tp.price.amount),
    quantity: 1,
    package_details: '',
    bundle_vs_standalone: 'Standalone',
    monthly_installment: safelyConvertValueToRSD(tp.price.installmentPrice),
    binding_period: tp.price.installments,
    user_type: '',
    payment_method: '',
    device_recycle: 'No',
  };
};

const parseInsuranceFromBasket = (insurance: any) => {
  return {
    item_id: 'Standard Device Insurance Package',
    item_name: 'Standard Device Insurance Package',
    affiliation: 'WebshopE2E',
    item_brand: 'Yettel',
    item_category: 'Device Insurance',
    item_category5: getUserType(),
    item_list_id: '',
    item_list_name: '',
    price: safelyConvertValueToRSD(insurance.price.amount),
    quantity: 1,
    package_details: '',
    monthly_installment: safelyConvertValueToRSD(insurance.price.installmentPrice),
    binding_period: insurance.price.installments,
    user_type: '',
    payment_method: '',
    device_recycle: 'No',
  };
};

const generateDevicePrice = (device: any) => {
  const discountedAmount = device.price?.discounts?.reduce((a: number, b: number) => a + b, 0);
  return safelyConvertValueToRSD(device.price.amount - discountedAmount);
};

const generatePriceRecycleValue = (device: any) => {
  const greenVoucher = device.price?.discounts?.find((discount: any) => discount.type === 'greenVoucher');

  if (greenVoucher) {
    return safelyConvertValueToRSD(device.price.amount - greenVoucher.amount);
  }

  return '';
};

const parseDeviceFromBasket = (device: any) => {
  return {
    item_id: device.id,
    item_name: device.name,
    affiliation: 'WebshopE2E',
    coupon: '',
    item_brand: device.manufacturer,
    item_category: Accessories.includes(device.type) ? 'Accessory' : 'Devices',
    item_category2: '',
    item_category3: '',
    item_category4: 'Postpaid',
    item_category5: getUserType(),
    item_list_id: '',
    item_list_name: '',
    item_variant: device.color?.color ?? '',
    price: generateDevicePrice(device),
    price_recycle: generatePriceRecycleValue(device),
    price_regular: safelyConvertValueToRSD(device.price.amount),
    quantity: 1,
    package_details: '',
    bundle_vs_standalone: 'Standalone',
    upfront_fee: 0,
    monthly_installment: safelyConvertValueToRSD(device.price.installmentPrice),
    binding_period: device.price.installments,
    user_type: '',
    payment_method: '',
    number_ownership: '',
    number_type: '',
    device_recycle: 'No',
  };
};

export const isDevice = (item: any) => {
  return [...Accessories, OrderItemsTypes.PHONE].includes(item.type);
};

const parseBasketItem = (item: any) => {
  if (item.type === OrderItemsTypes.TARIFF_PACKAGE) {
    return parseTarrifPackageFromBasket(item);
  }

  if (item.type === OrderItemsTypes.INSURANCE) {
    return parseInsuranceFromBasket(item);
  }

  if (isDevice(item)) {
    return parseDeviceFromBasket(item);
  }

  return null;
};

export const parseInsuranceItemForAnalytics = (item: any) => {
  return {
    item_id: 'Standard Device Insurance Package',
    item_name: 'Standard Device Insurance Package',
    affiliation: 'Webshop E2E',
    item_brand: 'Yettel',
    item_category: 'Device Insurance',
    item_category5: getUserType(),
    price: item.price,
    quantity: 1,
  };
};

export const parseBasketForAnalytics = (basketItems: any[]) => {
  const parsedBasketItems = basketItems.map(parseBasketItem).filter((nonNullItem) => nonNullItem);

  const tariffPackage = first(basketItems.filter((item) => item.type === OrderItemsTypes.TARIFF_PACKAGE));
  if (tariffPackage) {
    parsedBasketItems.map((item) => {
      item!.package_details = getPackageDetails(tariffPackage);
      return item;
    });
  }

  const participation = first(basketItems.filter((item) => item.type === OrderItemsTypes.COSMOS_PARTICIPATION));
  if (participation) {
    const participationAmount = safelyConvertValueToRSD(participation.price.amount);

    parsedBasketItems.map((item) => {
      if (isDevice(item)) {
        // @ts-ignore
        item!.upfront_fee = participationAmount;
      }

      return item;
    });
  }

  return parsedBasketItems as any[];
};

export const getBasketValue = (basketItems: any[]) => {
  const priceObjects = basketItems.map((item) => item?.price).filter((item) => item);
  const amounts = priceObjects.map((item) => item?.amount).filter((item) => item);
  const amountSum = amounts.reduce((a, b) => a + b, 0);

  return safelyConvertValueToRSD(amountSum);
};

export const getTotalBenefitsForSelectedTp = (tp: ISoftOfferPackage) => {
  const discountsAsString = JSON.stringify(tp.benefits);

  const calculatedDiscountCaughtStrings = discountsAsString.match(/calculated":\d*/g);
  if (calculatedDiscountCaughtStrings) {
    return Number(calculatedDiscountCaughtStrings.map((result) => result.split(':')[1]).reduce((a, b) => a + b));
  }
  return 0;
};

export const getTPTypeByName = (tpName: string) => {
  if (tpName.includes('Total')) {
    return 'Total packages';
  }

  if (tpName.includes('Go Smart')) {
    return 'Go Smart packages';
  }

  if (tpName.includes('Go')) {
    return 'Go packages';
  }

  return 'Internet';
};

type GetCampaignTypeFunction = (offer: SoftOffer) => AnalyticsCampaignType;

export const getCampaignTypeFromSoftOfferPackage: GetCampaignTypeFunction = (offer) => {
  if (offer.p2p || offer.p2pDj) return 'Pre2Postpaid';
  if (offer.upsell) return 'TP-upsell';
  return 'Soft-renewal';
};

export const createPayloadForCampaignAnalytics = (tp: ISoftOfferPackage, campaignType: AnalyticsCampaignType) => {
  const totalDiscount = getTotalBenefitsForSelectedTp(tp as ISoftOfferPackage);

  const analyticsData: Partial<GA4EcommerceItem> = {
    item_id: tp?.id,
    item_name: tp?.name,
    item_brand: 'Yettel',
    item_category: 'Packages',
    item_category2: campaignType,
    item_category3: getTPTypeByName(tp?.name || ''),
    item_category4: 'Postpaid',
    item_category5: 'Consumer',
    item_list_id: '',
    item_list_name: '',
    price: tp?.subscription,
    discount: totalDiscount,
    package_details: '',
    bundle_vs_standalone: 'Standalone',
    monthly_installment: Number(tp?.subscription) - totalDiscount,
    binding_period: 24,
    user_type: '',
  };

  analyticsData.package_details = `${analyticsData.item_name}|${analyticsData.item_id}|${analyticsData.item_category}|${analyticsData.item_category2}|${analyticsData.item_category3}|${analyticsData.item_category4}|${analyticsData.item_category5}`;

  return analyticsData;
};
